import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "clinicalShiftSelect",
    "courseSelect",
    "departmentDisplay",
    "competenciesContainer",
    "formDataSection",
    "submitButton",
    "signatureSection",
    "signaturePadContainer",
    "qrCodeContainer",
    "signatureData",
  ];

  connect() {
    console.log("✓✓✓ Patient Encounter Form controller connected ✓✓✓");
    console.log("Course select target exists:", this.hasCourseSelectTarget);
    console.log("Competencies container target exists:", this.hasCompetenciesContainerTarget);
    this.setupCourseChangeListener();
    this.setupClinicalShiftListener();
    this.setupSignatureListener();
    this.checkSubmitButtonState();
  }

  setupCourseChangeListener() {
    if (this.hasCourseSelectTarget) {
      this.courseSelectTarget.addEventListener("change", (event) => {
        this.loadCompetencies(event);
      });
    }
  }

  setupClinicalShiftListener() {
    if (!this.hasClinicalShiftSelectTarget || !this.hasCourseSelectTarget) {
      return;
    }

    this.clinicalShiftSelectTarget.addEventListener("change", () => {
      this.syncCourseFromShift();
      this.syncDepartmentFromShift();
    });
    this.syncCourseFromShift();
    this.syncDepartmentFromShift();
  }

  syncDepartmentFromShift() {
    if (!this.hasClinicalShiftSelectTarget || !this.hasDepartmentDisplayTarget) {
      return;
    }

    const selectedOption = this.clinicalShiftSelectTarget.selectedOptions[0];
    const departmentName = selectedOption?.dataset?.departmentName;
    this.departmentDisplayTarget.value = departmentName || "—";
  }

  syncCourseFromShift() {
    const selectedOption = this.clinicalShiftSelectTarget.selectedOptions[0];
    const courseId = selectedOption?.dataset?.courseId;
    if (!courseId) {
      return;
    }

    const courseChanged = this.courseSelectTarget.value !== courseId;
    this.courseSelectTarget.value = courseId;
    if (courseChanged) {
      this.courseSelectTarget.dispatchEvent(new Event("change"));
    }
  }

  async loadCompetencies(event) {
    const courseId = event.target.value;

    if (!courseId) {
      this.clearCompetencies();
      return;
    }

    try {
      // Show loading state
      this.showLoading();

      const response = await fetch(
        `/patient_encounters/competencies_for_course?course_id=${courseId}`,
        {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load competencies");
      }

      const data = await response.json();
      this.renderGroupedCompetencies(data.grouped);
    } catch (error) {
      console.error("Error loading competencies:", error);
      this.showError("Failed to load competencies. Please try again.");
    }
  }

  renderGroupedCompetencies(groupedData) {
    if (!this.hasCompetenciesContainerTarget) {
      console.error("Competencies container not found");
      return;
    }

    if (!groupedData || groupedData.length === 0) {
      this.competenciesContainerTarget.innerHTML = `
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle me-2"></i>
          No competencies available for this course.
        </div>
      `;
      return;
    }

    // Build HTML for grouped competencies
    let html = `
      <p style="margin-bottom: 0.4rem; color: #6b7280; font-size: 0.7rem; line-height: 1.2;">
        <i class="fas fa-info-circle" style="margin-right: 0.25rem; font-size: 0.65rem;"></i>Select competencies demonstrated during this encounter.
      </p>
      <div id="competency-items">
    `;

    let globalIndex = 0;
    groupedData.forEach((group, groupIdx) => {
      // Group header
      const isFirst = groupIdx === 0;
      html += `
        <div class="competency-group-header" style="${isFirst ? 'padding-top: 0; margin-top: 0; margin-bottom: 0.6rem;' : 'margin-top: 0.75rem; padding-top: 0.5rem; margin-bottom: 0.6rem; border-top: 1px solid #e5e7eb;'}">
          <h5 style="font-size: 0.8125rem; margin-bottom: 0; color: #6610f2; font-weight: 600; line-height: 1.2;">
            <i class="fas fa-certificate" style="font-size: 0.75rem; margin-right: 0.3rem;"></i>${this.escapeHtml(group.main_number)} ${this.escapeHtml(group.group_title || "Competency Group")}
          </h5>
        </div>
      `;

      // Render each competency in the group
      group.competencies.forEach((competency) => {
        html += `
          <div class="card competency-card" style="margin-bottom: 0.35rem; border-radius: 4px; border-left: 3px solid #6610f2;">
            <div class="card-body" style="padding: 0.4rem 0.5rem;">
              <div class="form-check" style="margin-bottom: 0; padding-left: 1.4rem; min-height: auto;">
                <input class="form-check-input" 
                       type="checkbox" 
                       name="competencies[${globalIndex}][selected]"
                       id="competency_${competency.id}"
                       value="1"
                       data-competency-id="${competency.id}"
                       style="width: 0.9rem; height: 0.9rem; margin-top: 0.1rem; margin-left: -1.4rem;">
                <label class="form-check-label" for="competency_${competency.id}" style="font-size: 0.8125rem; line-height: 1.15; margin-bottom: 0; font-weight: 600; cursor: pointer;">
                  ${this.escapeHtml(competency.name)}
                </label>
                <input type="hidden" 
                       name="competencies[${globalIndex}][learning_objective_id]"
                       value="${competency.id}">
              </div>
              
              ${
                competency.description
                  ? `
                <p style="font-size: 0.7rem; line-height: 1.2; margin: 0.15rem 0 0 1.4rem; color: #6b7280;">
                  ${this.escapeHtml(competency.description)}
                </p>
              `
                  : ""
              }
            </div>
          </div>
        `;
        globalIndex++;
      });
    });

    html += `</div>`;

    // Add compact styles
    html += `
      <style>
        /* MAXIMUM compactness CSS */
        #competency-items * {
          margin: 0 !important;
          padding: 0 !important;
        }
        
        #competency-items .card {
          margin-bottom: 0.35rem !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04) !important;
          padding: 0 !important;
        }
        
        #competency-items .card-body {
          padding: 0.4rem 0.5rem !important;
        }
        
        #competency-items .form-check {
          padding-left: 1.4rem !important;
          min-height: auto !important;
        }
        
        #competency-items .form-check-input {
          width: 0.9rem !important;
          height: 0.9rem !important;
          margin-top: 0.1rem !important;
          margin-left: -1.4rem !important;
        }
        
        #competency-items .form-check-label {
          font-size: 0.8125rem !important;
          line-height: 1.15 !important;
          cursor: pointer;
          font-weight: 600 !important;
        }
        
        #competency-items p {
          font-size: 0.7rem !important;
          line-height: 1.2 !important;
          margin: 0.15rem 0 0 1.4rem !important;
          color: #6b7280 !important;
        }
        
        .hidden {
          display: none !important;
        }
        
        .competency-card {
          transition: all 0.1s ease !important;
        }
        
        .competency-card:hover {
          box-shadow: 0 2px 4px rgba(102, 16, 242, 0.08) !important;
        }
        
        .form-check-input:checked ~ .form-check-label {
          color: #6610f2 !important;
        }
        
      </style>
    `;

    this.competenciesContainerTarget.innerHTML = html;
  }

  renderCompetencies(competencies) {
    if (!this.hasCompetenciesContainerTarget) {
      console.error("Competencies container not found");
      return;
    }

    if (competencies.length === 0) {
      this.competenciesContainerTarget.innerHTML = `
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle me-2"></i>
          No competencies available for this course.
        </div>
      `;
      return;
    }

    // Build HTML for competency checkboxes
    let html = `
      <p class="mb-3 text-muted">
        <i class="fas fa-info-circle me-2"></i>
        Select all competencies you demonstrated during this patient encounter.
      </p>
      <div id="competency-items">
    `;

    competencies.forEach((competency, index) => {
      html += `
        <div class="card mb-3 competency-card">
          <div class="card-body">
            <div class="form-check mb-3">
              <input class="form-check-input" 
                     type="checkbox" 
                     name="competencies[${index}][selected]"
                     id="competency_${competency.id}"
                     value="1"
                     data-competency-id="${competency.id}"
>
              <label class="form-check-label" for="competency_${competency.id}">
                <strong>${this.escapeHtml(competency.name)}</strong>
              </label>
              <input type="hidden" 
                     name="competencies[${index}][learning_objective_id]"
                     value="${competency.id}">
            </div>
            
            ${
              competency.description
                ? `
              <p class="small text-muted mb-3">
                ${this.escapeHtml(competency.description)}
              </p>
            `
                : ""
            }
            
          </div>
        </div>
      `;
    });

    html += `</div>`;
    this.competenciesContainerTarget.innerHTML = html;
  }

  showLoading() {
    if (this.hasCompetenciesContainerTarget) {
      this.competenciesContainerTarget.innerHTML = `
        <div class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2 text-muted">Loading competencies...</p>
        </div>
      `;
    }
  }

  clearCompetencies() {
    if (this.hasCompetenciesContainerTarget) {
      this.competenciesContainerTarget.innerHTML = `
        <div class="alert alert-info">
          <i class="fas fa-info-circle me-2"></i>
          Select a course to load available competencies
        </div>
      `;
    }
  }

  showError(message) {
    if (this.hasCompetenciesContainerTarget) {
      this.competenciesContainerTarget.innerHTML = `
        <div class="alert alert-danger">
          <i class="fas fa-exclamation-circle me-2"></i>
          ${this.escapeHtml(message)}
        </div>
      `;
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // Form validation before submit
  validateForm(event) {
    const checkedCompetencies = this.element.querySelectorAll(
      "input[data-competency-id]:checked"
    );

    if (checkedCompetencies.length === 0) {
      event.preventDefault();
      alert("Please select at least one competency before submitting.");
      return false;
    }

    return true;
  }

  // Show signature pad
  showSignaturePad(event) {
    event.preventDefault();
    
    // Update button states
    this.element.querySelectorAll('.signature-option-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    event.currentTarget.classList.add('active');
    
    // Show/hide containers
    if (this.hasSignaturePadContainerTarget) {
      this.signaturePadContainerTarget.classList.remove('d-none');
    }
    if (this.hasQrCodeContainerTarget) {
      this.qrCodeContainerTarget.classList.add('d-none');
    }
  }

  // Show QR code
  showQRCode(event) {
    event.preventDefault();
    
    // Update button states
    this.element.querySelectorAll('.signature-option-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    event.currentTarget.classList.add('active');
    
    // Show/hide containers
    if (this.hasSignaturePadContainerTarget) {
      this.signaturePadContainerTarget.classList.add('d-none');
    }
    if (this.hasQrCodeContainerTarget) {
      this.qrCodeContainerTarget.classList.remove('d-none');
    }
  }

  // Setup signature listener
  setupSignatureListener() {
    // Listen for signature-changed event from signature pad controller
    this.element.addEventListener('signature-changed', (event) => {
      console.log('Signature changed event received', event.detail);
      this.checkSubmitButtonState();
    });
    
    if (this.hasSignatureDataTarget) {
      // Also listen for input events as a backup
      this.signatureDataTarget.addEventListener('input', () => {
        this.checkSubmitButtonState();
      });
      
      this.signatureDataTarget.addEventListener('change', () => {
        this.checkSubmitButtonState();
      });
    }
  }

  // Check if submit button should be enabled
  checkSubmitButtonState() {
    if (this.hasSubmitButtonTarget && this.hasSignatureDataTarget) {
      const hasSignature = this.signatureDataTarget.value && this.signatureDataTarget.value.trim() !== '';
      this.submitButtonTarget.disabled = !hasSignature;
      
      if (hasSignature) {
        this.submitButtonTarget.classList.add('has-signature');
      } else {
        this.submitButtonTarget.classList.remove('has-signature');
      }
    }
  }

  // Submit for review
  submitForReview(event) {
    event.preventDefault();
    
    if (!this.hasSignatureDataTarget || !this.signatureDataTarget.value) {
      alert('Please obtain a signature before submitting for review.');
      return;
    }
    
    if (!confirm('Are you ready to submit this encounter for review? You will not be able to make further changes after submission.')) {
      return;
    }
    
    // Set status to submitted and submit the form
    const form = this.element;
    const statusField = document.createElement('input');
    statusField.type = 'hidden';
    statusField.name = 'patient_encounter[status]';
    statusField.value = 'submitted';
    form.appendChild(statusField);
    
    form.submit();
  }
}
